<template>
  <div class="max-w-4xl mx-auto my-5 border rounded-2xl px-6 py-6 bg-white dark:bg-gray-600 dark:border-gray-600">
    <div class="flex items-start justify-between gap-4 mb-5">
      <h2 class="text-2xl font-semibold dark:text-gray-100">{{ editMode ? 'Edit Your Profile' : 'Your Profile' }}
      </h2>
      <div class="shrink-0 text-right">
        <button v-if="!editMode" @click="toggleEditMode"
          class="dark:text-gray-600 dark:hover:text-gray-100 dark:bg-yellow-300 dark:hover:bg-yellow-600 px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-700">
          Edit
        </button>
        <button v-else @click="toggleEditMode"
          class="px-6 py-2 mr-4 bg-red-500 hover:bg-red-700 text-white font-bold rounded">
          Cancel
        </button>
        <button v-if="editMode" @click="updateProfile"
          class="px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-700">Update
          Profile</button>
      </div>
    </div>
    <form @submit.prevent="editMode && updateProfile()">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- User Information -->
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Username</label>
          <input type="text" v-model="user.username" disabled class="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 
                disabled:cursor-not-allowed disabled:opacity-70">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">First Name</label>
          <input type="text" v-model="user.firstName" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Last Name</label>
          <input type="text" v-model="user.lastName" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Initials</label>
          <input type="text" v-model="user.initials" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Default Job Type</label>
          <select v-model="user.jobType" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
            <option v-for="jobtype in jobTypes" :key="jobtype.id" :value="jobtype.id">
              {{ jobtype.name }}
            </option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Email Address</label>
          <input type="email" v-model="user.email" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
        </div>
        <div v-if="!isCoManagedTech">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Cell Email</label>
          <input type="email" v-model="user.cellEmail" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Phone Primary</label>
          <input type="tel" v-model="user.mobilePhone" :disabled="!editMode" @blur="validatePhone" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
          <p v-if="phoneError && editMode" class="text-red-500 text-xs mt-1">{{ phoneError }}</p>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Phone Primary Ext</label>
          <input type="text" v-model="user.extension" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Phone Secondary</label>
          <input type="tel" v-model="user.secondaryPhone" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Phone Secondary Ext</label>
          <input type="text" v-model="user.secondaryExt" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Notification Preference</label>
          <select v-model="user.notificationPreference" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
            <option>Email</option>
            <option>SMS</option>
            <option>Both</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Email Preference</label>
          <select v-model="user.ticketemailpref" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
            <option value="0">None</option>
            <option value="1">New Only</option>
            <option value="2">Updates Only</option>
            <option value="3">New and Updates</option>
          </select>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Tickets Per Page</label>
          <select v-model="user.ticketsperpage" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="15">15</option>
            <option value="20">20</option>
            <option value="25">25</option>
            <option value="30">30</option>
            <option value="35">35</option>
            <option value="40">40</option>
            <option value="45">45</option>
            <option value="50">50</option>
            <option value="75">75</option>
            <option value="100">100</option>
          </select>
        </div>
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">AIM/Misc Default Job
            Description</label>
          <textarea type="text" v-model="user.miscjobtext" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]" :placeholder="'(ticket/email administration)'"></textarea>
        </div>
        <div v-if="!isCoManagedTech">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">RC Username</label>
          <input type="text" v-model="user.rcusername" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]" :placeholder="'Your Username in Rocket.Chat'">
        </div>
        <div v-if="!isCoManagedTech">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Role</label>
          <input type="text" v-model="user.role" class="mt-1 block w-full rounded-md border-gray-300 shadow-sm 
                bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 
                disabled:cursor-not-allowed disabled:opacity-70" disabled>
        </div>
        <!-- Emergency Contact -->
        <div class="md:col-span-2" v-if="!isCoManagedTech">
          <h3 class="text-xl font-semibold mt-8 mb-5 dark:text-gray-100">Emergency Contact</h3>
        </div>
        <div v-if="!isCoManagedTech">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Name</label>
          <input type="text" v-model="user.emergencyContact.name" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
        </div>
        <div v-if="!isCoManagedTech">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Relation</label>
          <input type="text" v-model="user.emergencyContact.relation" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
        </div>
        <div v-if="!isCoManagedTech">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Phone</label>
          <input type="tel" v-model="user.emergencyContact.phone" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
        </div>

        <!-- Shifts Management Section -->
        <div class="md:col-span-2" v-if="!isCoManagedTech">
          <h3 class="text-xl font-semibold mt-8 mb-5 dark:text-gray-100">Shift Management</h3>
        </div>
        <div class="md:col-span-2" v-if="!isCoManagedTech">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Shift Type</label>
          <select v-model="selectedShiftType" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
            <option v-for="shiftType in shiftTypes" :key="shiftType.id" :value="shiftType.id">{{ shiftType.name }}
            </option>
          </select>
        </div>
        <div class="md:col-span-2" v-if="!isCoManagedTech">
          <table class="min-w-full mt-5" v-if="this.shifts.length > 0">
            <thead>
              <tr>
                <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Day</th>
                <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Start Time</th>
                <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">End Time</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(shift, index) in shifts" :key="shift.id">
                <td class="px-4 py-2">
                  <select v-model="shift.day_of_week" :disabled="!editMode" :class="[
                    'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
                    editMode
                      ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
                      : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
                  ]">
                    <option v-for="day in weekdays" :key="day.id" :value="day.id">{{ day.day }}</option>
                  </select>
                </td>
                <td class="px-4 py-2">
                  <input type="time" v-model="shift.begin_time" :disabled="!editMode" :class="[
                    'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
                    editMode
                      ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
                      : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
                  ]">
                </td>
                <td class="px-4 py-2">
                  <input type="time" v-model="shift.end_time" :disabled="!editMode" :class="[
                    'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
                    editMode
                      ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
                      : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
                  ]">
                </td>
                <td class="px-4 py-2 text-right">
                  <button @click="removeShift(index)" class="text-red-600 hover:text-red-900" v-if="editMode">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                      stroke="currentColor" class="w-5 h-5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          <div class="mt-4">
            <button @click="addShift" class="text-green-600 hover:text-green-900" v-if="editMode">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                stroke="currentColor" class="w-6 h-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>
        <!-- End of Shifts Management Section -->

        <!-- Password Change -->
        <div class="md:col-span-2">
          <h3 class="text-xl font-semibold mt-8 mb-5 dark:text-gray-100">Change Password (Leave blank if no password
            change desired)</h3>
        </div>
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">New Password</label>
          <input type="password" v-model="user.newPassword" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
        </div>
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-100">Confirm Password</label>
          <input type="password" @blur="validatePasswords" v-model="user.confirmPassword" :disabled="!editMode" :class="[
            'mt-1 block w-full rounded-md border-gray-300 shadow-sm',
            editMode
              ? 'bg-white dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100'
              : 'bg-gray-100 dark:bg-gray-500 dark:border-gray-900 dark:text-gray-300 cursor-not-allowed opacity-70'
          ]">
          <p v-if="passwordError && editMode" class="text-red-500 text-xs mt-1">{{ passwordError }}</p>
        </div>


        <div v-if="userStats && !isCoManagedTech" class="md:col-span-2 mt-12">
          <h3 class="text-xl font-semibold mb-5 dark:text-gray-100">Your Stats</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700 dark:text-gray-100">

            <!-- Leave Section -->
            <div class="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg dark:bg-opacity-50">
              <h4 class="font-semibold mb-2">Leave</h4>
              <p>Vacation/Personal: <span class="font-bold">{{ Number(userStats.leavebalance).toFixed(1) }}</span></p>
              <p>EPST (Sick Time): <span class="font-bold">{{ Number(userStats.epstbalance).toFixed(1) }}</span></p>
            </div>

            <!-- History Section -->
            <div class="bg-green-50 dark:bg-green-900 p-4 rounded-lg dark:bg-opacity-50">
              <h4 class="font-semibold mb-2">History</h4>
              <p>Last Quarter: <span class="font-bold">{{ Math.round(userStats.lastqtr).toLocaleString() }}</span></p>
              <p>Lifetime Hours: <span class="font-bold">{{ Math.round(userStats.lifetime).toLocaleString() }}</span>
              </p>
              <p>Jobs: <span class="font-bold">{{ Math.round(userStats.jobsLife).toLocaleString() }}</span></p>
              <p>Tickets All-Time: <span class="font-bold">{{ Math.round(userStats.closedTicketsLife).toLocaleString()
                  }}</span></p>
            </div>
          </div>
        </div>

        <div class="md:col-span-2 text-right">
          <button v-if="!editMode" @click="toggleEditMode"
            class="dark:text-gray-600 dark:hover:text-gray-100 dark:bg-yellow-300 dark:hover:bg-yellow-600 mt-6 px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-700">
            Edit
          </button>
          <button v-else @click="toggleEditMode"
            class="mt-6 px-6 py-2 mr-4 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded">
            Cancel
          </button>
          <button v-if="editMode" type="submit"
            class="mt-6 px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-700">Update
            Profile</button>
        </div>
      </div>
    </form>

    <div v-if="canSeeApiDocs"
      class="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-300">
      <a :href="apiDocsUrl" target="_blank" rel="noopener noreferrer"
        class="inline-flex items-center gap-2 text-yellow-600 hover:text-yellow-700 dark:text-yellow-400 dark:hover:text-yellow-300 underline">
        API Documentation (Swagger)
      </a>
    </div>

    <div v-if="canConnectAutomation"
      class="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700">
      <h2 class="text-lg font-semibold dark:text-gray-100">Automation platform</h2>
      <p class="mt-1 text-sm text-gray-600 dark:text-gray-300">
        Connect the AIM Automation platform to act on your behalf in ATMS. It can do only what your
        account can do, and you can revoke its access at any time.
      </p>

      <div v-if="automationRefreshToken"
        class="mt-4 rounded-md border border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-700 p-3">
        <p class="text-sm font-medium text-yellow-800 dark:text-yellow-200">
          Copy this token now — it will not be shown again. Paste it into AIM Automation.
        </p>
        <div class="mt-2 flex items-center gap-2">
          <code class="flex-1 break-all text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded p-2">{{ automationRefreshToken }}</code>
          <button type="button" @click="copyAutomationToken"
            class="shrink-0 px-3 py-2 text-xs font-medium rounded-md bg-yellow-500 hover:bg-yellow-600 text-white">Copy</button>
        </div>
      </div>

      <p v-if="automationError" class="mt-3 text-sm text-red-600 dark:text-red-400">{{ automationError }}</p>

      <div class="mt-4">
        <button type="button" @click="connectAutomation" :disabled="automationBusy"
          class="px-4 py-2 text-sm font-medium rounded-md bg-yellow-500 hover:bg-yellow-600 text-white disabled:opacity-50 disabled:cursor-not-allowed">
          {{ automationBusy ? 'Connecting…' : 'Connect automation platform' }}
        </button>
      </div>

      <div v-if="automationCreds.length" class="mt-4">
        <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300">Connected credentials</h3>
        <ul class="mt-2 divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-md">
          <li v-for="cred in automationCreds" :key="cred.id"
            class="flex items-center justify-between px-3 py-2 text-sm">
            <span class="text-gray-800 dark:text-gray-200">
              {{ cred.name }}
              <span class="text-gray-500 dark:text-gray-400">
                — last used {{ cred.last_used_at ? new Date(cred.last_used_at).toLocaleString() : 'never' }}
              </span>
            </span>
            <button type="button" @click="revokeAutomation(cred.id)"
              class="text-red-600 hover:text-red-700 dark:text-red-400 text-xs font-medium">Revoke</button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
export default {
  data() {
    return {
      user: {
        username: '',
        firstName: '',
        lastName: '',
        role: '',
        initials: '',
        email: '',
        cellEmail: '',
        mobilePhone: '',
        extension: '',
        secondaryPhone: '',
        secondaryExt: '',
        notificationPreference: '',
        ticketemailpref: 0,
        ticketsperpage: 20,
        jobType: 2,
        miscjobtext: '',
        rcusername: '',
        shiftType: 1,
        emergencyContact: {
          name: '',
          relation: '',
          phone: '',
        },
        newPassword: '',
        confirmPassword: ''
      },
      userroles: [],
      jobTypes: [],
      shifts: [],
      shiftTypes: [],
      weekdays: [],
      selectedShiftType: 1,
      query: '',
      editMode: false,
      phoneError: '',
      passwordError: '',
      userStats: null,
      automationCreds: [],
      automationRefreshToken: null,
      automationBusy: false,
      automationError: null,
    };
  },
  computed: {
    isCoManagedTech() {
      return this.userroles.includes(80);
    },
    canSeeApiDocs() {
      return this.userroles.some(r => [10, 20, 30].includes(Number(r)));
    },
    canConnectAutomation() {
      // Internal-scope staff only (mirrors the backend hasInternalScope gate; the
      // backend is the real check — this just hides the UI for everyone else).
      return this.userroles.some(r => [10, 20, 30, 40, 50].includes(Number(r)));
    },
    apiDocsUrl() {
      const apiBase = (process.env.VUE_APP_APIURL || '');
      const token = localStorage.getItem('token') || '';
      return `${apiBase}documentation?api_token=${encodeURIComponent(token)}`;
    }
  },
  methods: {
    fetchAutomationCredentials() {
      axios.get('integrations/automation')
        .then((res) => { this.automationCreds = (res.data || []).filter((c) => !c.revoked_at); })
        .catch(() => { /* non-fatal: non-internal users get 403 */ });
    },
    connectAutomation() {
      this.automationBusy = true;
      this.automationError = null;
      axios.post('integrations/automation/connect', {})
        .then((res) => {
          this.automationRefreshToken = res.data.refresh_token;
          this.fetchAutomationCredentials();
        })
        .catch((err) => {
          this.automationError = err.response?.data?.message || 'Could not connect the automation platform.';
        })
        .finally(() => { this.automationBusy = false; });
    },
    revokeAutomation(id) {
      if (!confirm('Revoke this automation credential? The platform will stop working until it is reconnected.')) return;
      axios.delete(`integrations/automation/${id}`)
        .then(() => { this.fetchAutomationCredentials(); })
        .catch((err) => {
          this.automationError = err.response?.data?.message || 'Could not revoke the credential.';
        });
    },
    copyAutomationToken() {
      if (this.automationRefreshToken && navigator.clipboard) {
        navigator.clipboard.writeText(this.automationRefreshToken);
      }
    },
    toggleEditMode() {
      this.editMode = !this.editMode;
      if (!this.editMode) {
        this.fetchUserInfo(); // Restore original data on cancel
        this.fetchJobTypes();
        this.fetchUserShifts();
        this.newPassword = '';
        this.confirmPassword = '';
      }
    },
    updateProfile() {
      if (this.validatePhone() && this.validatePasswords()) {
        this.$updateLoading(1);
        const userId = localStorage.getItem('user_id');
        axios.put(`user/${userId}`, {
          user: this.user,
          shifts: this.shifts
        })
          .then(response => {
            this.editMode = false;
            this.fetchUserInfo();
            this.fetchJobTypes();
            this.fetchUserShifts();
            this.$updateLoading(-1);
          })
          .catch(error => {
            // Handle error
            this.$updateLoading(-1);
            console.error('There was an error updating the profile:', error);
            alert('Failed to update profile.');
          });
      }
    },
    validatePhone() {
      const re = /^(\d{3}-\d{3}-\d{4}|\d{10})$/;
      if (!re.test(this.user.mobilePhone)) {
        this.phoneError = 'Invalid phone format. Use 000-000-0000 or 0000000000.';
        return false;
      }
      this.phoneError = '';
      return true;
    },
    validatePasswords() {
      if (this.user.newPassword !== this.user.confirmPassword) {
        this.passwordError = 'Passwords do not match.';
        return false;
      }
      this.passwordError = '';
      return true;
    },
    fetchUserInfo() {
      const user_id = localStorage.getItem('user_id');
      this.$updateLoading(1);
      try {
        axios.get(`user/role/${user_id}`)
          .then(response => {
            this.userroles = response.data.roles;
          })
          .catch(error => {
            console.error(error);
          });
        axios.get(`user/profile/${user_id}`)
          .then(response => {
            this.user.username = response.data.username;
            this.user.firstName = response.data.firstname;
            this.user.lastName = response.data.lastname;
            this.user.initials = response.data.initials;
            this.user.email = response.data.email;
            this.user.cellEmail = response.data.cellemail;
            this.user.address = response.data.address;
            this.user.role = response.data.role_name;
            const splitPhone = (combined) => {
              const s = String(combined || '');
              const m = s.match(/^(.+?)\s+x(.+)$/i);
              return m ? { phone: m[1].trim(), ext: m[2].trim() } : { phone: s, ext: '' };
            };
            const primary = splitPhone(response.data.phone);
            const secondary = splitPhone(response.data.phone_secondary);
            this.user.mobilePhone = primary.phone;
            this.user.extension = primary.ext || response.data.extension || '';
            this.user.secondaryPhone = secondary.phone;
            this.user.secondaryExt = secondary.ext;
            this.user.notificationPreference = response.data.notificationpreference;
            this.user.ticketemailpref = response.data.ticketemailpref;
            this.user.jobType = response.data.jobtype_id;
            this.user.ticketsperpage = response.data.ticketsperpage;
            this.user.miscjobtext = response.data.miscjobtext;
            this.user.rcusername = response.data.rcusername;
            this.user.emergencyContact.name = response.data.emergency_name;
            this.user.emergencyContact.relation = response.data.emergency_relation;
            this.user.emergencyContact.phone = response.data.emergency_phone;
            //for the address search function
            this.query = response.data.address;
          })
          .catch(error => {
            console.error('There was an error fetching the user data:', error);
          });
      } catch (error) {
        console.error('Error fetching tickets:', error);
      } finally {
        this.$updateLoading(-1);
      }
    },
    fetchUserShifts() {
      const user_id = localStorage.getItem('user_id');
      axios.get(`user/shifts/${user_id}`) // Adjust the endpoint as needed for your API
        .then(response => {
          // Assuming the response has a data object with user information
          console.log(response)
          this.shifts = response.data.shifts;
          this.shiftTypes = response.data.shifttypes;
          this.weekdays = response.data.weekdays;
          this.user.shiftType = this.shifts[0].shifttype_id;
        })
        .catch(error => {
          console.error('There was an error fetching the user data:', error);
        });
    },
    fetchUserStats() {
      this.$updateLoading(1);
      axios.get('job/stats')
        .then(response => {
          this.userStats = response.data;
        })
        .catch(error => {
          console.error('Error fetching user stats:', error);
        })
        .finally(() => {
          this.$updateLoading(-1);
        });
    },
    fetchJobTypes() {
      axios.get(`user/jobtypes/`) // Adjust the endpoint as needed for your API
        .then(response => {
          // Assuming the response has a data object with user information
          console.log(response)
          this.jobTypes = response.data;
        })
        .catch(error => {
          console.error('There was an error fetching the user data:', error);
        });
    },
    addShift() {
      this.shifts.push({
        id: null,
        user_id: null,
        day_of_week: this.weekdays[0].id,
        begin_time: '08:00:00',
        end_time: '16:00:00',
        shifttype_id: this.selectedShiftType,
        shifttype: this.shiftTypes.find(st => st.id === this.selectedShiftType)
      });
    },
    removeShift(index) {
      this.shifts.splice(index, 1);
    }
  },
  mounted() {
    this.fetchUserInfo();
    this.fetchJobTypes();
    this.fetchUserShifts();
    if (!this.isCoManagedTech) {
      this.fetchUserStats();
    }
    this.fetchAutomationCredentials();
  }
};
</script>

<style scoped>
/* Add your styles here */
</style>
